import React, { Fragment, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Spinner, Alert } from 'reactstrap';
import { Breadcrumbs, H4 } from '../../../../AbstractElements';
import quizRewardApi from '../../../../api/quizReward';
import appQuizApi from '../../../../api/appQuiz';
import RewardCard from './RewardCard';
import RewardModal from './RewardModal';
import RewardFilters from './RewardFilters';


const RewardManagementContain = () => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Filters
  const [quizIdFilter, setQuizIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [quizOptions, setQuizOptions] = useState([]);
  const [quizOptionsLoading, setQuizOptionsLoading] = useState(false);
  
  // Modal
  const [modal, setModal] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  
  // Form data
  const defaultRewardForm = {
    quizId: '',
    totalQuestions: 3,
    correctAnswers: 3,
    message: 'tặng TOPUP 10K từ RVOPV',
    rewardType: 'topup',
    rewardValue: {
      topup: 10000,
      description: 'Xuất sắc! Bạn đã chinh phục toàn bộ câu hỏi 🎉'
    },
    quantity: '',
    isActive: true,
    sortOrder: 1
  };

  const [formData, setFormData] = useState(defaultRewardForm);

  // Load rewards
  useEffect(() => {
    loadRewards();
  }, [quizIdFilter, statusFilter]);

  useEffect(() => {
    loadQuizOptions();
  }, []);

  const loadRewards = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (quizIdFilter) params.quizId = parseInt(quizIdFilter);
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';
      
      const response = await quizRewardApi.getAllRewards(params);
      
      // Handle new API response structure
      let allRewards = [];
      if (response.data?.rewardsByQuiz) {
        // Flatten rewards from rewardsByQuiz structure
        response.data.rewardsByQuiz.forEach(quizGroup => {
          if (quizGroup.rewards) {
            // Ensure quantity is preserved when flattening
            const rewardsWithQuantity = quizGroup.rewards.map(reward => ({
              ...reward,
              quantity: reward.quantity !== undefined ? reward.quantity : null
            }));
            allRewards = [...allRewards, ...rewardsWithQuantity];
          }
        });
      } else {
        allRewards = response.data?.rewards || response.rewards || response.data || response;
      }
      
      setRewards(Array.isArray(allRewards) ? allRewards : []);
    } catch (err) {
      console.error('❌ Error loading rewards:', err);
      setError('Không thể tải danh sách rewards');
      setRewards([]);
    } finally {
      setLoading(false);
    }
  };

  const loadQuizOptions = async () => {
    try {
      setQuizOptionsLoading(true);
      const response = await appQuizApi.getAllQuizTemplates({ limit: 500 });
      const quizzes =
        response?.data?.quizzes ||
        response?.data ||
        response ||
        [];

      const options = Array.isArray(quizzes)
        ? quizzes
            .map((quiz) => {
              const id = quiz.id || quiz.quizId;
              if (!id) return null;
              const name = quiz.name || quiz.title || `Quiz #${id}`;
              return { id, name };
            })
            .filter(Boolean)
        : [];

      const sorted = options.sort((a, b) => a.id - b.id);
      setQuizOptions(sorted);

      // Không tự động chọn quiz để người dùng chủ động lựa chọn
    } catch (err) {
      console.error('❌ Error loading quiz options:', err);
    }
    finally {
      setQuizOptionsLoading(false);
    }
  };

  const toggleModal = () => {
    const nextState = !modal;
    setModal(nextState);

    if (nextState) {
      // Refresh quiz list every time modal opens to ensure latest quizzes are available
      loadQuizOptions();
      setEditingReward(null);
      resetForm();
    }
  };

  const getNextSortOrder = (quizId) => {
    if (!quizId) return 1;
    const rewardsForQuiz = rewards.filter((r) => r.quizId === Number(quizId));
    if (rewardsForQuiz.length === 0) return 1;
    const maxOrder = Math.max(...rewardsForQuiz.map((r) => r.sortOrder || 0));
    return maxOrder + 1;
  };

  const resetForm = () => {
    setFormData({
      ...defaultRewardForm,
      quizId: '',
      quantity: '',
      sortOrder: 1
    });
  };

  const handleEdit = (reward) => {
    setEditingReward(reward);
    const rewardValue =
      typeof reward.rewardValue === 'object' && reward.rewardValue !== null
        ? reward.rewardValue
        : (() => {
            switch (reward.rewardType) {
              case 'topup':
                return { topup: Number(reward.rewardValue) || 0, description: '' };
              case 'points':
                return { points: Number(reward.rewardValue) || 0, description: '' };
              case 'badge':
                return { badgeName: String(reward.rewardValue || ''), badgeIcon: '🏆', description: '' };
              case 'voucher':
                return { voucherCode: String(reward.rewardValue || ''), description: '' };
              case 'gift':
                return { giftName: String(reward.rewardValue || ''), giftValue: 0, description: '' };
              case 'coupon':
                return { couponCode: String(reward.rewardValue || ''), discountValue: 0, description: '' };
              case 'discount':
                return { discountPercent: Number(reward.rewardValue) || 0, maxDiscount: 0, description: '' };
              case 'freeship':
                return { maxValue: Number(reward.rewardValue) || 0, minOrder: 0, description: '' };
              default:
                return { description: '' };
            }
          })();

    setFormData({
      quizId: reward.quizId,
      totalQuestions: reward.totalQuestions || 3,
      correctAnswers: reward.correctAnswers || 0,
      message: reward.message || '',
      rewardType: reward.rewardType || 'topup',
      rewardValue,
      quantity: reward.quantity !== null && reward.quantity !== undefined ? reward.quantity : '',
      isActive: reward.isActive ?? true,
      sortOrder: reward.sortOrder || 1
    });
    setModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      
      // Handle quantity: allow null, empty string becomes null, valid numbers are parsed
      // Note: quantity can be 0, so we need to check explicitly for null/undefined/empty string
      let quantityValue = null;
      if (formData.quantity === '' || formData.quantity === null || formData.quantity === undefined) {
        quantityValue = null;
      } else {
        // formData.quantity is a number (could be 0, 1, 2, etc.)
        // Use Number() instead of parseInt() to handle both integers and ensure proper conversion
        const numValue = Number(formData.quantity);
        quantityValue = isNaN(numValue) ? null : numValue;
      }

      const payload = {
        quizId: parseInt(formData.quizId) || 0,
        totalQuestions: parseInt(formData.totalQuestions) || 0,
        correctAnswers: parseInt(formData.correctAnswers) || 0,
        message: formData.message,
        rewardType: formData.rewardType,
        rewardValue: formData.rewardValue,
        isActive: Boolean(formData.isActive),
        sortOrder: parseInt(formData.sortOrder) || 1
      };

      // Always include quantity in payload, even if null or 0
      // This ensures the field is always sent to the API
      if (quantityValue !== null && quantityValue !== undefined) {
        payload.quantity = quantityValue;
      } else {
        payload.quantity = null;
      }


      if (!payload.quizId) {
        setError('Vui lòng chọn Quiz để gắn reward.');
        return;
      }
      
      if (editingReward) {
        try {
          const result = await quizRewardApi.updateReward(editingReward.id, payload);
          setSuccess('Cập nhật reward thành công!');
        } catch (updateErr) {
          console.error('❌ Update error:', updateErr);
          console.error('❌ Update error response:', updateErr.response);
          throw updateErr;
        }
      } else {
        await quizRewardApi.createReward(payload);
        setSuccess('Tạo reward thành công!');
      }
      
      toggleModal();
      loadRewards();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error saving reward:', err);
      console.error('❌ Error details:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Không thể lưu reward');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa reward này?')) return;
    
    try {
      await quizRewardApi.deleteReward(id);
      setSuccess('Xóa reward thành công!');
      loadRewards();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error deleting reward:', err);
      setError('Không thể xóa reward');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await quizRewardApi.toggleRewardActive(id);
      setSuccess('Cập nhật trạng thái thành công!');
      loadRewards();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error toggling status:', err);
      setError('Không thể cập nhật trạng thái');
    }
  };

  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs parent='Quiz' title='Reward Management' mainTitle='Reward Management' />
        <Container fluid={true}>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 mb-0">Đang tải rewards...</p>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumbs parent='Quiz' title='Reward Management' mainTitle='Quản lý Quiz Rewards' />
      <Container fluid={true}>
        {/* Success/Error Messages */}
        {success && <Alert color="success" fade={false} timeout={300}>{success}</Alert>}
        {error && <Alert color="danger" fade={false} timeout={300}>{error}</Alert>}

        {/* Header Card */}
        <Card className="shadow-sm border-0 mb-4">
          <CardBody className="p-4">
            <Row className="align-items-center">
              <Col md="6">
                <H4 className="text-dark mb-0">
                  <i className="fa fa-trophy me-2 text-primary"></i>
                  Quản lý Quiz Rewards
                </H4>
              </Col>
              <Col md="6" className="text-end">
                <Button color="primary" onClick={toggleModal}>
                  <i className="fa fa-plus me-2"></i>
                  Tạo Reward Mới
                </Button>
              </Col>
            </Row>

            {/* Filters */}
            <RewardFilters 
              quizIdFilter={quizIdFilter}
              setQuizIdFilter={setQuizIdFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
            />
          </CardBody>
        </Card>

        {/* Rewards List */}
        <Row>
          {rewards.length === 0 ? (
            <Col>
              <Card className="text-center py-5">
                <CardBody>
                  <i className="fa fa-trophy text-muted" style={{fontSize: '3rem'}}></i>
                  <p className="text-muted mt-3">Chưa có reward nào</p>
                </CardBody>
              </Card>
            </Col>
          ) : (
            rewards.map((reward) => (
              <Col lg="6" key={reward.id}>
                <RewardCard 
                  reward={reward}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                />
              </Col>
            ))
          )}
        </Row>

        {/* Create/Edit Modal */}
        <RewardModal 
          isOpen={modal}
          toggle={toggleModal}
          editingReward={editingReward}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          quizOptions={quizOptions}
          quizOptionsLoading={quizOptionsLoading}
          getNextSortOrder={getNextSortOrder}
        />
      </Container>
    </Fragment>
  );
};

export default RewardManagementContain;

